import {
  Card,
  Title,
  Subtitle,
  BarChart,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Badge,
} from "@tremor/react";
import { Trophy, MousePointerClick } from "lucide-react";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { TableScroll } from "@/components/ui/TableScroll";
import { deriveTopClienti } from "@/data/derived";
import { useFilterVersion } from "@/data/filters";
import { useDrilldown } from "@/components/drilldown/Drilldown";
import { eur, eurAxis, procent, numar } from "@/lib/format";

const SEGMENT_BADGE: Record<string, { color: "blue" | "slate" | "violet"; label: string }> = {
  export: { color: "blue", label: "Export" },
  intern: { color: "slate", label: "Intern" },
  mixt: { color: "violet", label: "Mixt" },
};

export function TopClientiSection() {
  useFilterVersion();
  const { openDrill } = useDrilldown();
  const clienti = deriveTopClienti();
  const top = clienti.slice(0, 10);

  const chartVenit = top
    .slice(0, 8)
    .map((c) => ({ client: c.client, valoare: c.client, Venit: Math.round(c.venit_eur) }));

  return (
    <section>
      <SectionHeader
        id="top-clienti"
        icon={Trophy}
        titlu="6. Top clienți — venit & marjă"
        descriere="Clasament pe venit facturat, cu marja generată — cine ține cifra de afaceri și cine e profitabil."
      />

      <div className="mb-3 flex items-center gap-1.5 text-xs text-slate-400">
        <MousePointerClick className="h-3.5 w-3.5" />
        Click pe o bară / rând → comenzile clientului și marja lui.
      </div>

      <Card className="ring-slate-200">
        <Title>Venit per client (top 8)</Title>
        <Subtitle>Pe perioada și filtrele curente</Subtitle>
        <BarChart
          className="mt-4 h-80 w-full"
          data={chartVenit}
          index="client"
          categories={["Venit"]}
          colors={["blue"]}
          valueFormatter={eurAxis}
          layout="vertical"
          showLegend={false}
          yAxisWidth={132}
          noDataText="Fără date pe filtrul curent"
          onValueChange={(v) => {
            const valoare = (v as { valoare?: string } | null)?.valoare;
            if (valoare) openDrill({ type: "client", valoare });
          }}
        />
      </Card>

      <Card className="mt-4 ring-slate-200">
        <Title>Clasament clienți</Title>
        <Subtitle>Venit, marjă și pondere din total — click pe rând pentru detaliu</Subtitle>
        <TableScroll minWidth="56rem">
          <Table className="mt-3">
            <TableHead>
              <TableRow className="border-slate-200">
                <TableHeaderCell className="w-10">#</TableHeaderCell>
                <TableHeaderCell>Client</TableHeaderCell>
                <TableHeaderCell>Segment</TableHeaderCell>
                <TableHeaderCell className="text-right">Comenzi</TableHeaderCell>
                <TableHeaderCell className="text-right">Venit</TableHeaderCell>
                <TableHeaderCell className="text-right">Marjă</TableHeaderCell>
                <TableHeaderCell className="text-right">Marjă %</TableHeaderCell>
                <TableHeaderCell className="text-right">% din venit</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {top.map((c, i) => {
                const seg = SEGMENT_BADGE[c.segment];
                return (
                  <TableRow
                    key={c.client}
                    onClick={() => openDrill({ type: "client", valoare: c.client })}
                    className="cursor-pointer border-slate-100 transition-colors hover:bg-brand-50/60"
                  >
                    <TableCell>
                      <Badge size="xs" color={i < 3 ? "amber" : "gray"}>{i + 1}</Badge>
                    </TableCell>
                    <TableCell className="font-medium text-slate-800">{c.client}</TableCell>
                    <TableCell>
                      <Badge size="xs" color={seg.color}>{seg.label}</Badge>
                    </TableCell>
                    <TableCell className="text-right tabular">{c.nr_comenzi}</TableCell>
                    <TableCell className="text-right tabular text-slate-700">{eur(c.venit_eur, 0)}</TableCell>
                    <TableCell className="text-right tabular font-medium text-slate-800">{eur(c.marja_eur, 0)}</TableCell>
                    <TableCell className="text-right">
                      <Badge size="xs" color={c.marja_pct >= 25 ? "emerald" : c.marja_pct >= 10 ? "amber" : "rose"}>
                        {procent(c.marja_pct)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular text-slate-500">{numar(c.pondere_venit_pct, 1)}%</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableScroll>
      </Card>
    </section>
  );
}
